import { Fragment, useMemo, useState } from 'react';

import {
  COLOR_TOKENS,
  type ColorToken,
  CountPill,
  HorizontalMenuTabs,
  LabeledFilterSelect,
  LegendRow,
  StackedBar,
  type StackedBarSegment,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  type AyotHeatDimension,
  type AyotHeatGrade,
  type AyotHeatMap,
  type AyotTenure,
  type StudentWhereClause,
} from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';

import { Box, Link, type SxProps, type Theme, Tooltip, Typography } from '@mui/material';

import { ayotLevelLabel, ayotTrackLabel, percentOf } from './analytics.labels';
import { AnalyticsCard, type InfoNote } from './AnalyticsCard';
import { academicInfo, breakAfterSlashes } from './OnTrackHeatMapInfo';
import { type StudentDataHandle, useStudentData } from './useStudentData';
import {
  ayotAcademicBandColumns,
  ayotDimensionLevelColumns,
  COLUMNS,
  COMMON_COLUMNS,
  type StudentColumn,
} from './viewData.columns';

const { heatMap } = appStrings.modules.coachingAnalytics.onTrack;
const { viewData, hover } = appStrings.modules.coachingAnalytics;

const CARD_ID = 'coaching-analytics-card-ayot-heat-map';
const SEMI_SKILLED = 'semi-skilled';

const SCALE: readonly ColorToken[] = [
  'Warning/100',
  'Warning/300',
  'Orange/300',
  'Orange/400',
  'Orange/500',
];
const LIGHT_TEXT_FROM = 3;
// the level a student has not reached needs no rubric text, so its column takes a fraction of the others
const UNMET_LEVEL = 'unmet';
const UNMET_COLUMN_SHARE = 0.3;
// In a card narrower than NARROW_CARD_WIDTH, cells keep these widths and the grid
// scrolls; squeezed further, a rubric wraps a word per line.
const NARROW_CARD_WIDTH = 700;
const CELL_MIN_WIDTH = 180;
const UNMET_CELL_MIN_WIDTH = 72;

const LEVEL_BAR_HEIGHT = 24;
const LEVEL_SEGMENTS: readonly { key: string; color: ColorToken; labelColor: ColorToken }[] = [
  { key: UNMET_LEVEL, color: 'Gray/200', labelColor: 'Colors/Text/text-primary' },
  { key: 'minimum', color: 'BlueDark/300', labelColor: 'Colors/Base/White' },
  { key: 'advanced', color: 'BlueDark/500', labelColor: 'Colors/Base/White' },
];

type ScoredGrade = AyotHeatGrade & Readonly<{ tenure: AyotTenure }>;

const scaleIndex = (pct: number): number =>
  pct <= 0 ? 0 : Math.min(SCALE.length - 1, Math.ceil((pct / 100) * SCALE.length) - 1);

/** The student field each band row's buckets are values of; null values are the `not_on_file` band. */
const BAND_FIELDS: Readonly<Record<string, StudentWhereClause['field']>> = {
  rigor: 'coursework_rigor',
  major_rigor: 'major_rigor',
  selectivity: 'school_selectivity',
};
const NOT_ON_FILE = 'not_on_file';
const BAND_COLUMNS: Readonly<Record<string, readonly StudentColumn[]>> = {
  rigor: [COLUMNS.courseworkRigor],
  major_rigor: [COLUMNS.majorRigor],
  selectivity: [COLUMNS.schoolSelectivity],
};

/** Display name for a row: academic measures are keyed by id, dimensions carry their name. */
const rowLabel = (dimension: AyotHeatDimension, tenure: AyotTenure | null): string => {
  if (dimension.kind === 'level') return dimension.name;
  if (dimension.id === 'gpa' && tenure === 'post_secondary')
    return heatMap.measures.gpaPostSecondary;

  return heatMap.measures[dimension.id] ?? dimension.name;
};

const bucketLabel = (dimension: AyotHeatDimension, bucket: string): string => {
  if (dimension.kind === 'level') return ayotLevelLabel(bucket);
  if (dimension.kind === 'band') return dimension.bucket_names?.[bucket] ?? bucket;

  return ayotTrackLabel(bucket);
};

const bucketWhere = (dimension: AyotHeatDimension, bucket: string): StudentWhereClause => {
  if (dimension.kind === 'level') {
    return { field: 'ayot_dimension_levels', key: dimension.id, op: 'eq', value: bucket };
  }
  if (dimension.kind === 'band') {
    const field = BAND_FIELDS[dimension.id] ?? 'ayot_academic_bands';

    return bucket === NOT_ON_FILE ? { field, op: 'is_null' } : { field, op: 'eq', value: bucket };
  }

  return { field: 'ayot_academic_bands', key: dimension.id, op: 'eq', value: bucket };
};

/** Standards written per career track for a dimension that is scored by level. */
const trackStandardsOf = (dimension: AyotHeatDimension, grade: ScoredGrade): string[] =>
  dimension.kind === 'level'
    ? Object.entries(dimension.standards[grade.tenure] ?? {})
        .filter(([key]) => !dimension.buckets.includes(key))
        .map(([track, text]) => `${ayotTrackLabel(track)}: ${text}`)
    : [];

/**
 * Hover text for a row: the stored definition (else the product copy for that
 * measure), plus how the row is scored when its standard varies by career track.
 */
const definitionOf = (dimension: AyotHeatDimension, grade: ScoredGrade): string[] => {
  const key = dimension.kind === 'level' ? dimension.name : dimension.id;
  const definition =
    dimension.definition[grade.tenure] ?? heatMap.definitions[grade.tenure][key] ?? null;
  const scoring =
    dimension.kind === 'band'
      ? heatMap.bandScored.hover
      : trackStandardsOf(dimension, grade).length > 0
        ? heatMap.trackScored.hover
        : null;

  return [...(definition ? [definition] : []), ...(scoring ? [scoring] : [])];
};

/**
 * Rubric text for a cell: the grade's criteria when known, else the tenure's
 * standard for that bucket. A dimension whose standard is written per career
 * track is scored against the student's own track, so its level cells carry the
 * scoring rule instead of a single criterion.
 */
const rubricOf = (
  dimension: AyotHeatDimension,
  bucket: string,
  grade: ScoredGrade,
): string | null => {
  if (dimension.kind === 'band') return heatMap.bandScored.cell[dimension.id]?.[bucket] ?? null;

  const criteria = dimension.criteria.find(
    (entry) => entry.grade_id === grade.id && entry.level === bucket,
  );

  if (criteria) return criteria.text;

  const standard = dimension.standards[grade.tenure]?.[bucket];

  if (standard) return standard;

  return trackStandardsOf(dimension, grade).length > 0
    ? (heatMap.trackScored.cell[bucket] ?? null)
    : null;
};

/** Column widths: a narrow track for the unmet level, an equal share for every other bucket. */
const columnTemplate = (buckets: readonly string[], narrow: boolean): string =>
  buckets
    .map((bucket) => {
      const isUnmet = bucket === UNMET_LEVEL;
      const floor = narrow ? (isUnmet ? UNMET_CELL_MIN_WIDTH : CELL_MIN_WIDTH) : 0;

      return `minmax(${floor}px, ${isUnmet ? UNMET_COLUMN_SHARE : 1}fr)`;
    })
    .join(' ');

const HeatCell = ({
  id,
  dimension,
  bucket,
  grade,
  count,
  total,
  onClick,
}: Readonly<{
  id: string;
  dimension: AyotHeatDimension;
  bucket: string;
  grade: ScoredGrade;
  count: number;
  total: number;
  onClick: () => void;
}>) => {
  const pct = percentOf(count, total);
  const index = scaleIndex(pct);
  const rubric = rubricOf(dimension, bucket, grade);
  const isClickable = count > 0;
  const isLight = index >= LIGHT_TEXT_FROM;

  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Typography variant="text-xs-semibold">
          {`${bucketLabel(dimension, bucket)} — ${heatMap.cell(count, total, pct)}`}
        </Typography>
      }
    >
      <Box
        id={id}
        sx={cellSx(isClickable)}
        style={{ backgroundColor: COLOR_TOKENS[SCALE[index]] }}
        onClick={
          isClickable
            ? (event) => {
                event.stopPropagation();
                onClick();
              }
            : undefined
        }
      >
        <CountPill value={`${pct === 0 && count > 0 ? '<1' : pct}%`} sx={cellPctSx} />
        {rubric ? (
          <Typography variant="text-sm-regular" sx={cellRubricSx(isLight)}>
            {rubric}
          </Typography>
        ) : null}
      </Box>
    </Tooltip>
  );
};

const HeatTable = ({
  rows,
  buckets,
  grade,
  students,
  segmentTitle,
  divider = false,
}: Readonly<{
  rows: readonly AyotHeatDimension[];
  buckets: readonly string[];
  grade: ScoredGrade;
  students: StudentDataHandle;
  segmentTitle: string;
  divider?: boolean;
}>) => {
  if (rows.length === 0) return null;

  const { tenure } = grade;

  const openCell = (dimension: AyotHeatDimension, bucket: string) => {
    const where: StudentWhereClause[] = [
      bucketWhere(dimension, bucket),
      { field: 'assessed_track', op: 'neq', value: SEMI_SKILLED },
      { field: 'assessment_grade', op: 'eq', value: grade.value },
    ];

    students.openSegment({
      label: viewData.segmentLabel(
        `${segmentTitle} · ${rowLabel(dimension, tenure)}`,
        bucketLabel(dimension, bucket),
      ),
      where,
    });
  };

  return (
    <Box sx={scrollSx}>
      <Box sx={gridSx(columnTemplate(buckets, false), columnTemplate(buckets, true))}>
        {divider && <Box sx={dividerSx} />}
        <Box />
        {buckets.map((bucket) => (
          <Typography key={bucket} variant="text-sm-medium" sx={columnLabelSx}>
            {bucketLabel(rows[0], bucket)}
          </Typography>
        ))}
        {rows.map((dimension) => {
          const counts = dimension.counts.by_grade[String(grade.id)];
          const total = counts?.total ?? 0;

          return (
            <Fragment key={dimension.id}>
              <Box sx={rowLabelCellSx}>
                <RowLabel dimension={dimension} grade={grade} />
              </Box>
              {buckets.map((bucket) => (
                <HeatCell
                  key={bucket}
                  id={`${CARD_ID}-cell-${dimension.id}-${bucket}`}
                  dimension={dimension}
                  bucket={bucket}
                  grade={grade}
                  count={counts?.[bucket] ?? 0}
                  total={total}
                  onClick={() => openCell(dimension, bucket)}
                />
              ))}
            </Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

const RowLabel = ({
  dimension,
  grade,
}: Readonly<{ dimension: AyotHeatDimension; grade: ScoredGrade }>) => {
  const definition = definitionOf(dimension, grade);
  const label = (
    <Typography variant="text-sm-medium" sx={rowLabelSx}>
      {breakAfterSlashes(rowLabel(dimension, grade.tenure))}
    </Typography>
  );

  if (definition.length === 0) return label;

  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Box sx={tooltipSx}>
          {definition.map((text) => (
            <Typography key={text} variant="text-xs-regular">
              {text}
            </Typography>
          ))}
        </Box>
      }
    >
      {label}
    </Tooltip>
  );
};

/** Level-scored rows as one stacked bar each: the share of students at Not yet, Proficient and Advanced. */
const LevelBars = ({
  rows,
  levels,
  grade,
  students,
  segmentTitle,
}: Readonly<{
  rows: readonly AyotHeatDimension[];
  levels: readonly string[];
  grade: ScoredGrade;
  students: StudentDataHandle;
  segmentTitle: string;
}>) => {
  if (rows.length === 0) return null;

  const segmentsShown = LEVEL_SEGMENTS.filter((segment) => levels.includes(segment.key));

  return (
    <Box sx={levelBarsSx}>
      <Box sx={dividerLineSx} />
      <Box sx={legendSx}>
        {segmentsShown.map((segment) => (
          <LegendRow key={segment.key} color={segment.color} label={ayotLevelLabel(segment.key)} />
        ))}
      </Box>
      {rows.map((dimension) => {
        const counts = dimension.counts.by_grade[String(grade.id)];
        const total = counts?.total ?? 0;
        const segments: StackedBarSegment[] = segmentsShown.map((segment) => {
          const count = counts?.[segment.key] ?? 0;
          const pct = percentOf(count, total);

          return {
            key: segment.key,
            id: `${CARD_ID}-cell-${dimension.id}-${segment.key}`,
            pct,
            color: segment.color,
            labelColor: segment.labelColor,
            tooltip: hover.count(ayotLevelLabel(segment.key), count),
            onClick:
              count > 0
                ? () =>
                    students.openSegment({
                      label: viewData.segmentLabel(
                        `${segmentTitle} · ${rowLabel(dimension, grade.tenure)}`,
                        ayotLevelLabel(segment.key),
                      ),
                      where: [
                        bucketWhere(dimension, segment.key),
                        { field: 'assessed_track', op: 'neq', value: SEMI_SKILLED },
                        { field: 'assessment_grade', op: 'eq', value: grade.value },
                      ],
                    })
                : undefined,
          };
        });

        return (
          <Box key={dimension.id} sx={levelBarRowSx}>
            <Box sx={levelBarLabelSx}>
              <RowLabel dimension={dimension} grade={grade} />
            </Box>
            <Box sx={levelBarWrapSx}>
              <StackedBar segments={segments} height={LEVEL_BAR_HEIGHT} />
            </Box>
            <Typography variant="text-sm-semibold" sx={levelBarTotalSx}>
              {total.toLocaleString()}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

/** The rubric as a heat map: one row per dimension, one shaded cell per level or track. */
export const OnTrackHeatMapCard = ({ map }: Readonly<{ map: AyotHeatMap }>) => {
  const [categoryIndex, setCategoryIndex] = useState(0);
  const [gradeId, setGradeId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const category = map.categories[categoryIndex] ?? map.categories[0];
  // grades arrive youngest first; the first with students is the default
  const grades = map.grades.filter(
    (entry): entry is ScoredGrade => entry.total > 0 && entry.tenure !== null,
  );
  const grade = grades.find((entry) => String(entry.id) === gradeId) ?? grades[0] ?? null;
  const tenure = grade?.tenure ?? null;

  const visible = useMemo(
    () =>
      (category?.dimensions ?? []).filter((dimension) => {
        if (tenure === null || grade === null || !dimension.tenures.includes(tenure)) return false;

        const scoredGrades = dimension.grade_ids?.[tenure];

        return !scoredGrades || scoredGrades.includes(grade.id);
      }),
    [category, tenure, grade],
  );
  const trackRows = visible.filter((dimension) => dimension.kind === 'track');
  const levelRows = visible.filter((dimension) => dimension.kind === 'level');
  const bandRows = visible.filter((dimension) => dimension.kind === 'band');
  // the track section is the summary; the rest of a category that has one is behind View more,
  // with its level-scored rows drawn as bars after the band rows
  const isAcademic = trackRows.length > 0;
  const isCollapsible = isAcademic && levelRows.length + bandRows.length > 0;
  const showDetail = !isCollapsible || expanded;

  const columns = useMemo(
    () => [
      ...COMMON_COLUMNS,
      COLUMNS.assessmentGrade,
      COLUMNS.assessedTrack,
      ...ayotAcademicBandColumns(
        trackRows.map((dimension) => ({
          key: dimension.id,
          name: rowLabel(dimension, tenure),
        })),
      ),
      ...bandRows.flatMap((dimension) => BAND_COLUMNS[dimension.id] ?? []),
      ...ayotDimensionLevelColumns(
        levelRows.map((dimension) => ({ key: dimension.id, name: dimension.name })),
      ),
    ],
    [trackRows, bandRows, levelRows, tenure],
  );

  const students = useStudentData(
    CARD_ID,
    category ? viewData.segmentLabel(heatMap.title, category.name) : heatMap.title,
    columns,
  );

  if (!category) return null;

  const excludedNotes: InfoNote[] = [
    map.excluded.semi_skilled > 0 && heatMap.info.semiSkilled(map.excluded.semi_skilled),
    map.no_grade > 0 && heatMap.info.noGrade(map.no_grade),
  ];
  // academics is the one category whose rows are scored three different ways
  const info: InfoNote[] =
    trackRows.length > 0 && tenure
      ? academicInfo(map, visible, tenure, excludedNotes)
      : [heatMap.info.rows, heatMap.info.grades, ...excludedNotes];

  return (
    <AnalyticsCard
      title={heatMap.title}
      subtitle={heatMap.subtitle(map.total)}
      infoTitle={category.name}
      info={info}
      viewData={students}
      belowHeader={
        <Box sx={controlsSx}>
          <HorizontalMenuTabs
            id={`${CARD_ID}-category-tabs`}
            variant="filter"
            containerSxOverride={tabsScrollSx}
            tabs={map.categories.map((entry) => entry.shortname ?? entry.name)}
            value={categoryIndex}
            setValue={setCategoryIndex}
          />
          <Box sx={pickerSx}>
            <LabeledFilterSelect
              id={`${CARD_ID}-grade-select`}
              label={heatMap.gradeLabel}
              hideLabel
              value={grade ? String(grade.id) : ''}
              options={grades.map((entry) => ({
                value: String(entry.id),
                label: entry.name,
                count: entry.total,
              }))}
              onChange={setGradeId}
            />
          </Box>
        </Box>
      }
    >
      {!grade ? (
        <Typography variant="text-sm-regular" sx={mutedSx}>
          {heatMap.noStudents}
        </Typography>
      ) : (
        <Box sx={tablesSx}>
          <HeatTable
            rows={trackRows}
            buckets={map.tracks}
            grade={grade}
            students={students}
            segmentTitle={category.name}
          />
          {showDetail && (
            <>
              {bandRows.map((dimension, index) => (
                <HeatTable
                  key={dimension.id}
                  rows={[dimension]}
                  buckets={dimension.buckets}
                  grade={grade}
                  students={students}
                  segmentTitle={category.name}
                  divider={index === 0 && trackRows.length > 0}
                />
              ))}
              {isAcademic ? (
                <LevelBars
                  rows={levelRows}
                  levels={map.levels}
                  grade={grade}
                  students={students}
                  segmentTitle={category.name}
                />
              ) : (
                <HeatTable
                  rows={levelRows}
                  buckets={map.levels}
                  grade={grade}
                  students={students}
                  segmentTitle={category.name}
                />
              )}
            </>
          )}
          {isCollapsible && (
            <Box sx={viewMoreRowSx}>
              <Link
                id={`${CARD_ID}-view-${expanded ? 'less' : 'more'}`}
                component="button"
                variant="text-sm-semibold"
                underline="none"
                sx={viewMoreSx}
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? heatMap.viewLess : heatMap.viewMore}
              </Link>
            </Box>
          )}
        </Box>
      )}
    </AnalyticsCard>
  );
};

const controlsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const tabsScrollSx: SxProps<Theme> = {
  minWidth: 0,
  maxWidth: '100%',
  overflowX: 'auto',
};

const pickerSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: 280,
};

const tablesSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  width: '100%',
});

const viewMoreRowSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
  width: '100%',
};

const viewMoreSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/700'],
  '&:hover': { textDecoration: 'underline' },
});

// spans the bucket columns only, leaving the row-label column clear
const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  gridColumn: '2 / -1',
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

const dividerLineSx: SxProps<Theme> = ({ backrs }) => ({
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  width: '100%',
});

const levelBarsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const legendSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: backrs.spacing['spacing-xl'],
  width: '100%',
});

const levelBarRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  '@container (min-width: 650px)': {
    flexDirection: 'row',
    alignItems: 'center',
    gap: backrs.spacing['spacing-lg'],
  },
});

const levelBarLabelSx: SxProps<Theme> = {
  display: 'flex',
  minWidth: 0,
  '@container (min-width: 650px)': {
    width: 200,
    flexShrink: 0,
  },
};

const levelBarWrapSx: SxProps<Theme> = {
  width: '100%',
  minWidth: 0,
  '@container (min-width: 650px)': {
    flex: 1,
  },
};

const levelBarTotalSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  minWidth: 40,
  textAlign: 'right',
  flexShrink: 0,
});

const gridSx =
  (columns: string, narrowColumns: string): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'grid',
    gridTemplateColumns: `minmax(96px, 150px) ${columns}`,
    alignItems: 'stretch',
    columnGap: backrs.spacing['spacing-lg'],
    rowGap: backrs.spacing['spacing-lg'],
    width: '100%',
    [`@container (max-width: ${NARROW_CARD_WIDTH - 1}px)`]: {
      gridTemplateColumns: `minmax(96px, 150px) ${narrowColumns}`,
    },
  });

const columnLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  textAlign: 'left',
});

const scrollSx: SxProps<Theme> = {
  width: '100%',
  overflowX: 'auto',
};

const rowLabelCellSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'sticky',
  left: 0,
  zIndex: 1,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  display: 'flex',
  alignItems: 'flex-start',
  minWidth: 0,
  paddingRight: backrs.spacing['spacing-md'],
  paddingTop: backrs.spacing['spacing-md'],
});

// sized to its text so the definition hover anchors to the words, not the whole label column
const rowLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  width: 'fit-content',
  maxWidth: '100%',
  overflowWrap: 'break-word',
});

const cellSx =
  (isClickable: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    minHeight: 40,
    padding: backrs.spacing['spacing-xl'],
    paddingRight: backrs.spacing['spacing-6xl'],
    cursor: isClickable ? 'pointer' : 'default',
    border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
    borderRadius: backrs.radius['radius-md'],
  });

const cellTextColor = (backrs: Theme['backrs'], isLight: boolean): string =>
  isLight ? backrs.colors['Colors/Base/White'] : backrs.colors['Colors/Text/text-primary'];

const cellPctSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: backrs.spacing['spacing-lg'],
  right: backrs.spacing['spacing-lg'],
});

const cellRubricSx =
  (isLight: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    overflowWrap: 'anywhere',
    color: cellTextColor(backrs, isLight),
  });

const tooltipSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const mutedSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
